<template>
    <div
        v-if="options && options.length > 0 && !isCategoryLabel(label)"
        :class="['pb-4 lg:pb-0', { 'lg:mb-6': isOpen }]">
        <button @click="isOpen = !isOpen" class="plp-filter">
            <span>{{ label }}</span>
            <IconChevronDown
                :class="{ '!-rotate-180': isOpen }"
                class="size-3 -rotate-90 transition-all lg:-rotate-0" />
        </button>
        <div
            class="overflow-hidden pt-1.5 lg:px-4 lg:duration-300"
            :class="{ 'filter-open': isOpen, 'filter-closed': !isOpen }"
            :aria-hidden="!isOpen"
            role="group">
            <div class="flex h-full flex-col">
                <div class="flex-1 overflow-y-auto">
                    <div class="mt-3 lg:hidden">
                        <button type="button" class="mb-8 flex items-center leading-none" @click="isOpen = false">
                            <ArrowLeftIcon class="mr-3 size-5 stroke-2" />
                            Terug
                        </button>
                        <span class="styled-as-h3 mb-5 block text-xl">{{ label }}</span>
                    </div>
                    <div class="mb-10 grid grid-cols-1 gap-x-16 lg:mb-0">
                        <template v-for="(option, index) in options" :key="option.id">
                            <div
                                class="mb-7 flex cursor-pointer items-center text-sm font-normal last:mb-0"
                                v-show="index < 5 || showMoreOptions">
                                <input
                                    type="checkbox"
                                    @change="invokeFilter(option.id, $event)"
                                    :id="option.id"
                                    class="checkbox relative mr-1.5 size-4 cursor-pointer appearance-none rounded border-2 border-black before:absolute before:h-full before:w-full before:bg-green-200 before:bg-[length:12px_12px] before:bg-center before:bg-no-repeat before:duration-200 before:content-[''] checked:before:bg-black checked:before:bg-[url('/check-solid.svg')] focus:outline-none"
                                    :disabled="
                                        keepFilters && !isOptionSelected(option.id) &&
                                        option.customFields?.nd_tweakwise_nrofresults === 0
                                    "
                                    :checked="isOptionSelected(option.id)" />
                                <label
                                    class="mb-0! flex flex-1 justify-between text-sm leading-0 font-normal"
                                    :for="option.id">
                                    <span>{{ option.translated.name }}</span>
                                    <span
                                        v-if="option.customFields?.nd_tweakwise_nrofresults"
                                        class="text-sm leading-0 font-normal"
                                        >({{ option.customFields.nd_tweakwise_nrofresults }})</span
                                    >
                                </label>
                            </div>
                        </template>
                        <button
                            v-if="options.length > 5"
                            @click="showMoreOptions = !showMoreOptions"
                            class="btn btn-underline !w-max !p-0 text-sm">
                            {{ showMoreOptions ? 'Minder tonen' : 'Meer tonen' }}
                        </button>
                    </div>
                </div>
                <div class="sticky right-0 bottom-0 left-0 bg-white pb-5 lg:hidden">
                    <button
                        type="button"
                        class="btn w-full bg-black text-white"
                        @click="
                            isOpen = false;
                            mobileFiltersMenuStore.closeMenu();
                        ">
                        {{ $snippets['headless.plp.view'] }} {{ total }} {{ $snippets['headless.plp.items'] }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    const keepFilters = useKeepFilters();
    import { ArrowLeftIcon } from '@heroicons/vue/24/outline';
    import { isCategoryLabel, parsePropertyIds } from '~/utils/plp';

    const props = withDefaults(
        defineProps<{
            label: string;
            code: string;
            index: number;
            options: Array<any> | undefined;
            currentFilters: any;
            total?: number;
        }>(),
        {
            options: () => [],
        },
    );
    const isOpen = defineModel<boolean>('isOpen', { default: true });
    const showMoreOptions = ref(false);
    const route = useRoute();
    const selectedOptions = computed(() => parsePropertyIds(route.query.properties));

    const mobileFiltersMenuStore = useMobileFiltersMenuStore();
    const emit = defineEmits(['invokeFilter']);

    const isOptionSelected = (id: string) => {
        return selectedOptions.value?.includes(id) ?? false;
    };

    const invokeFilter = (id: string, event?: Event) => {
        emit('invokeFilter', id, props.index);
    };
</script>
